import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import { RawEditorSettings } from 'tinymce';
import Input from '../../forms/Input';
import Button from '../../forms/Buttom';

import { ContentPage, SectionGalery } from './styles';
import { IEditResource, IPages } from './index';
import { TabItem } from '../Tabs';
import Upload from '../../forms/Upload';
import { IconFileAlt } from '../../../styles/icons';
import { useToast } from '../../../hooks/toast';
import { useAuth } from '../../../hooks/auth';
import { uploadFiles } from '../../../services/api';
import { escapeHtml } from '../../../utils/escapeHtml';

interface IProps {
  handlePage(page: IPages): void;
  propsEditor: RawEditorSettings;
  setIsOpen: () => void;
  editResource: IEditResource;
}

interface IInfographicData {
  url: string;
  width: string;
  height: string;
  type: 'iframe';
}

const parseInfographic = (resource?: string): IInfographicData | undefined => {
  if (!resource) {
    return undefined;
  }

  try {
    return JSON.parse(decodeURI(resource)) as IInfographicData;
  } catch {
    return undefined;
  }
};

const FormInfographic: React.FC<IProps> = ({
  handlePage,
  propsEditor,
  setIsOpen,
  editResource,
}) => {
  const formRef = useRef<FormHandles>(null);
  const { addToast } = useToast();
  const { user } = useAuth();

  const [selectedInfographic, setSelectedInfographic] = useState('');
  const [loadUpload, setLoadUpload] = useState(false);

  const isEdit = useMemo(() => {
    return Object.keys(editResource).length > 0;
  }, [editResource]);

  const editedInfographic = useMemo(
    () => parseInfographic(editResource.resource),
    [editResource.resource],
  );

  useEffect(() => {
    setSelectedInfographic(editedInfographic?.url || '');
  }, [editedInfographic]);

  const handleUpload = useCallback(
    async (files: File[]) => {
      setLoadUpload(true);
      try {
        const formData = new FormData();
        formData.append('infographics', files[0]);
        await uploadFiles({
          formData,
          provider_id: user.provider?.id || '123',
          type: 'infographics',
        }).then((res: any) => {
          const { data } = res;
          setSelectedInfographic(data.url);
        });

        addToast({
          title: 'Sucesso!',
          description: 'Infografico enviado com sucesso',
          type: 'success',
        });
      } catch (error) {
        addToast({
          title: 'Ops ...',
          description: 'Ocorreu um erro durante seu upload.',
          type: 'error',
        });
      } finally {
        setLoadUpload(false);
      }
    },
    [addToast, user.provider],
  );

  const handleSubmit = useCallback(
    async data => {
      try {
        const title = data.title?.trim();
        if (!title) {
          addToast({
            title: 'Ops ...',
            description: 'Informe o título do infográfico',
            type: 'error',
          });
          return;
        }
        if (!data.url || data.url === 'url') {
          addToast({
            title: 'Ops ...',
            description: 'Selecione arquivo zip com o infografico',
            type: 'error',
          });
          return;
        }
        const infographic: IInfographicData = {
          url: data.url,
          width: '100%',
          height: data.height || '650px',
          type: 'iframe',
        };

        if (isEdit) {
          editResource.element.setAttribute(
            'data-resource',
            encodeURI(JSON.stringify(infographic)),
          );
          editResource.element.setAttribute('data-subtype', 'infographic');
          editResource.element.textContent = title;
          propsEditor.insertContent(propsEditor.selection.getContent());
        } else {
          propsEditor.insertContent(
            `<div class="resource" data-interaction="true" data-type="iframe" data-subtype="infographic" data-resource="${encodeURI(
              JSON.stringify(infographic),
            )}">
                  ${escapeHtml(title)}
                </div>\n
                <p>&nbsp;</p>`,
          );
        }

        propsEditor.focus();
        setIsOpen();
      } catch (error) {
        addToast({
          title: 'Ops ...',
          description: 'Ocorreu um erro durante seu upload.',
          type: 'error',
        });
        propsEditor.focus();
      }
    },
    [propsEditor, addToast, setIsOpen, isEdit, editResource.element],
  );

  return (
    <Form
      ref={formRef}
      onSubmit={handleSubmit}
      initialData={{
        title: editResource.element?.textContent?.trim(),
        height: editedInfographic?.height || '650px',
      }}
    >
      <ContentPage>
        {!isEdit && (
          <button type="button" onClick={() => handlePage('main')}>
            Voltar
          </button>
        )}
        <SectionGalery>
          <h2>Infográfico</h2>
          <Input
            label="Título do infográfico"
            name="title"
            type="text"
            placeholder="Ex.: Linha do tempo do crescimento"
          />
          <Input
            hidden
            label=""
            name="url"
            defaultValue="url"
            onChange={e => setSelectedInfographic(e.target.value)}
            value={selectedInfographic}
          />
          <div className="container-pdf">
            <div className="wrap-icon">
              <IconFileAlt />
            </div>
            <h3>{selectedInfographic || `Selecione um infografico ...`}</h3>
          </div>
          <TabItem id="tab-01" defaultValue="tab-01">
            <div className="container-imges">
              <h2>Enviar infografico</h2>
              <Upload
                onUpload={handleUpload}
                formts={[{ extension: '.zip', types: 'application/zip' }]}
                loading={loadUpload}
              />
            </div>
          </TabItem>
          <Input
            label="Altura *"
            name="height"
            placeholder="Exemplo: (100px, 30px, etc...)"
          />
          <Input hidden label="" name="type" defaultValue="iframe" />
        </SectionGalery>
        <Button label="Inserir" type="submit" />
      </ContentPage>
    </Form>
  );
};

export default FormInfographic;
